/**
 * 예약 API 클라이언트 — chat-server `apps/chat-server/src/modules/reservation/*`.
 *
 * 자원 등록은 관리 콘솔(8444)에서만 하고, 사용자 사이트는 조회·신청·취소·승인만 한다.
 */
import { apiClient } from './client';

export type BookingUnit = 'time' | 'day';
export type ReservationStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface ReservationManagerDto {
  id: string;
  name: string;
  position: string | null;
  avatarUrl: string | null;
}

export interface ReservationResourceDto {
  id: string;
  category: string;
  name: string;
  location: string | null;
  equipmentInfo: string | null;
  capacity: number;
  bookingUnit: BookingUnit;
  slotMinutes: number;
  openMinute: number;
  closeMinute: number;
  requiresApproval: boolean;
  maxDaysAhead: number;
  isActive: boolean;
  sortOrder: number;
  owner: ReservationManagerDto | null;
  managers: ReservationManagerDto[];
}

export interface ReservationDto {
  id: string;
  resourceId: string;
  requesterId: string;
  startAt: string;
  endAt: string;
  quantity: number;
  purpose: string | null;
  status: ReservationStatus;
  decidedById: string | null;
  decidedAt: string | null;
  rejectReason: string | null;
  createdAt: string;
  requester: ReservationManagerDto | null;
  resource: { id: string; name: string; category: string; location: string | null; bookingUnit: BookingUnit } | null;
  /** 슬롯 조회 응답에서만 — 내 예약이면 true(타인 건은 사유가 가려진다). */
  mine?: boolean;
}

export async function listResources(): Promise<ReservationResourceDto[]> {
  const { data } = await apiClient.get<{ resources: ReservationResourceDto[] }>('/reservation/resources');
  return Array.isArray(data?.resources) ? data.resources : [];
}

/** 기간 내 예약 현황 — 월/일 뷰가 이 응답으로 잔여 수량까지 계산한다. */
export async function listResourceSlots(
  resourceId: string,
  from: Date,
  to: Date,
): Promise<{ resource: ReservationResourceDto; reservations: ReservationDto[] }> {
  const { data } = await apiClient.get<{ resource: ReservationResourceDto; reservations: ReservationDto[] }>(
    `/reservation/resources/${resourceId}/slots`,
    { params: { from: from.toISOString(), to: to.toISOString() } },
  );
  return {
    resource: data.resource,
    reservations: Array.isArray(data?.reservations) ? data.reservations : [],
  };
}

export async function createReservation(input: {
  resourceId: string;
  startAt: Date;
  endAt: Date;
  quantity?: number;
  purpose?: string | null;
  /**
   * 캘린더 연동 — 이 예약을 부른 일정. 캘린더에서 방을 잡을 때만 넘긴다.
   * ⭐ 서버가 그 일정의 «작성자인지» 검증하고, 일정이 지워지면 이 예약도 «함께 취소»된다.
   *   (2026-09-30 확인: `reservation.routes.ts` 스키마에 이 칸이 섰다.)
   */
  calendarEventId?: string;
  /**
   * 반복 일정의 회차(RRULE 원래 시각). ⛔ `calendarEventId` 와 «함께만» 보낼 수 있다 —
   * 혼자 보내면 서버가 400 `OCCURRENCE_WITHOUT_EVENT` 로 막는다.
   */
  occurrenceAt?: string | null;
}): Promise<ReservationDto> {
  const { data } = await apiClient.post<{ reservation: ReservationDto }>('/reservation/reservations', {
    resourceId: input.resourceId,
    startAt: input.startAt.toISOString(),
    endAt: input.endAt.toISOString(),
    ...(input.quantity !== undefined ? { quantity: input.quantity } : {}),
    ...(input.purpose ? { purpose: input.purpose } : {}),
    ...(input.calendarEventId ? { calendarEventId: input.calendarEventId } : {}),
    // ⛔ 짝을 여기서 «지킨다» — 회차만 혼자 가면 서버가 400 OCCURRENCE_WITHOUT_EVENT 로 막는다.
    //   보내는 쪽의 실수를 요청으로 만들지 않는다(정본과 같은 꼴).
    ...(input.calendarEventId && input.occurrenceAt ? { occurrenceAt: input.occurrenceAt } : {}),
  });
  return data.reservation;
}

export async function listMyReservations(params?: { status?: string; cursor?: string; limit?: number }): Promise<{
  reservations: ReservationDto[];
  nextCursor: string | null;
}> {
  const { data } = await apiClient.get<{ reservations: ReservationDto[]; nextCursor: string | null }>(
    '/reservation/reservations/mine',
    { params },
  );
  return {
    reservations: Array.isArray(data?.reservations) ? data.reservations : [],
    nextCursor: data?.nextCursor ?? null,
  };
}

export async function cancelReservation(id: string): Promise<ReservationDto> {
  const { data } = await apiClient.post<{ reservation: ReservationDto }>(`/reservation/reservations/${id}/cancel`);
  return data.reservation;
}

export async function listApprovals(params?: { status?: string; cursor?: string; limit?: number }): Promise<{
  reservations: ReservationDto[];
  nextCursor: string | null;
  managedResourceCount: number;
}> {
  const { data } = await apiClient.get<{
    reservations: ReservationDto[]; nextCursor: string | null; managedResourceCount: number;
  }>('/reservation/approvals', { params });
  return {
    reservations: Array.isArray(data?.reservations) ? data.reservations : [],
    nextCursor: data?.nextCursor ?? null,
    managedResourceCount: data?.managedResourceCount ?? 0,
  };
}

export async function decideReservation(id: string, approve: boolean, reason?: string | null): Promise<ReservationDto> {
  const { data } = await apiClient.post<{ reservation: ReservationDto }>(
    `/reservation/reservations/${id}/${approve ? 'approve' : 'reject'}`,
    approve ? {} : { reason: reason ?? null },
  );
  return data.reservation;
}

/**
 * 내가 담당인 자원 수 — 0 이면 "승인처리" 메뉴를 감춘다.
 * 사용자 단위 메뉴 권한 테이블이 없어 서버 판정을 받아 쓴다(프론트 숨김은 UX 용, 차단은 서버가 한다).
 */
export async function getManagerScope(): Promise<number> {
  const { data } = await apiClient.get<{ managedResourceCount: number }>('/reservation/me/manager-scope');
  return data?.managedResourceCount ?? 0;
}

/**
 * 하루치 자원 현황 — 대시보드 「회의실 현황」 위젯 (2026-09-09 · 판 5).
 *
 * ★★★ 자원마다 `/slots` 를 도는 대신 **왕복 한 번**이다. 「지금 비었나 · 다음이 언제인가」의
 *     판정은 화면이 한다 — 서버가 '지금' 을 굳히면 위젯 캐시(60초) 안에서 시각이 멎는다.
 * ★ `categories` 는 **거르기와 무관하게 전량**이라, 하나로 좁혀 본 상태에서도 갈아탈 수 있다.
 * ⚠ **옛 서버에는 이 라우트가 없다**(404·400). 부르는 쪽이 실패를 삼키고 위젯을 접는다.
 */
export interface ReservationStatusDto {
  resources: ReservationResourceDto[];
  reservations: ReservationDto[];
  categories: string[];
}

export async function getReservationStatus(
  from: Date,
  to: Date,
  category?: string,
): Promise<ReservationStatusDto> {
  const { data } = await apiClient.get<ReservationStatusDto>('/reservation/status', {
    params: { from: from.toISOString(), to: to.toISOString(), ...(category ? { category } : {}) },
  });
  return {
    resources: Array.isArray(data?.resources) ? data.resources : [],
    reservations: Array.isArray(data?.reservations) ? data.reservations : [],
    categories: Array.isArray(data?.categories) ? data.categories : [],
  };
}
